/**
 * Modo día / noche.
 *
 * La elección vive en este dispositivo (localStorage): el mismo usuario puede
 * querer el modo día en el móvil al sol y el de noche en el ordenador.
 * «Sistema» sigue lo que diga el sistema operativo y cambia con él.
 *
 * index.html aplica el tema antes de pintar nada para que no haya destello;
 * aquí se mantiene al día cuando cambia.
 */

import { useSyncExternalStore } from 'react';

export type ThemeChoice = 'system' | 'light' | 'dark';
export type Theme = 'light' | 'dark';

const KEY = 'uachess.theme';
const BAR_COLOR: Record<Theme, string> = { dark: '#131418', light: '#f4f5f7' };

const media = window.matchMedia('(prefers-color-scheme: light)');
const listeners = new Set<() => void>();

function read(): ThemeChoice {
  try {
    const value = localStorage.getItem(KEY);
    if (value === 'light' || value === 'dark') return value;
  } catch {
    /* sin almacenamiento: se sigue al sistema */
  }
  return 'system';
}

let choice = read();

function resolve(value: ThemeChoice): Theme {
  if (value === 'system') return media.matches ? 'light' : 'dark';
  return value;
}

function apply(): void {
  const theme = resolve(choice);
  document.documentElement.dataset.theme = theme;
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', BAR_COLOR[theme]);
  listeners.forEach((listener) => listener());
}

media.addEventListener('change', () => {
  if (choice === 'system') apply();
});

export function setThemeChoice(value: ThemeChoice): void {
  choice = value;
  try {
    if (value === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, value);
  } catch {
    /* se aplica igualmente en esta visita */
  }
  apply();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Elección guardada y tema que resulta de ella. */
export function useTheme(): { choice: ThemeChoice; theme: Theme } {
  const current = useSyncExternalStore(subscribe, () => choice);
  const theme = useSyncExternalStore(subscribe, () => resolve(choice));
  return { choice: current, theme };
}

apply();
